import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { Box, ButtonBase, Typography } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import { type FC, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { holidayForShift } from "@/components/team/Holidays/schedule/scheduleHolidays";
import { usePageScheduleHolidays } from "@/components/team/Holidays/schedule/scheduleHolidaysContext";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useIsTextEnlarged } from "@/hooks/useIsTextEnlarged";
import { useJobColors } from "@/hooks/useJobColors";
import { useRoleGroupColors } from "@/hooks/useRoleColors";
import { FONT_SIZE, GAPS_LIST, INK, LETTER_SPACING, MOTION, NEUTRAL, SURFACE, SURFACE_TINT } from "@/theme/tokens";
import MetaChip from "@/ui/Badges/MetaChip";
import { InlineHint } from "@/ui/molecules/InlineHint";
import { TOUCH_TARGET_HEIGHT_SX } from "@/ui/touchTarget";
import { getStaffingStatusStyle } from "../WeeklyGapView/CountBadge";
import { getSectionTone } from "../WeeklyGapView/consts";
import { GapsGroupPostButton } from "./GapsGroupPostButton";
import { GapsMissingRow } from "./GapsMissingRow";
import { GapsStaffRow } from "./GapsStaffRow";
import { formatStaffCount, type GapsMissingEntry, type GapsShiftGroup as GapsShiftGroupModel } from "./gapsDayModel";
import { type GapsScopedShiftGroup, isSectionWriteBlocked } from "./gapsGroupSheets";
import { useGapsCountsHidden } from "./useGapsCountsHidden";

/** The artifact's micro job badge: uppercase, tracked, tight. */
const jobBadgeSx = { textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, fontWeight: 600, flex: "none" } as const;

type Props = {
	/** With its section's scope, which says whether anything may be posted or added under it (`isSectionWriteBlocked`). */
	group: GapsScopedShiftGroup;
	isOpen: boolean;
	onToggle: () => void;
	onPostShift: (group: GapsShiftGroupModel) => void;
	onOpenStaff: (nurseInShiftId: string) => void;
	onAssignLocation: (nurseInShiftId: string) => void;
	onAddToMissing: (group: GapsShiftGroupModel, entry: GapsMissingEntry) => void;
	/** Shift ids the week's cost projection flagged as driving overtime, for the day being shown. */
	overtimeShiftIds: ReadonlySet<string>;
	/** The day is before today (`GapsHomeModel.isDayPast`): no `+`, since nobody could pick up a post for it. */
	isDayPast: boolean;
	/**
	 * Whether the holes are listed under the people. Not on a past day — a hole there can no longer be filled — and not
	 * while searching, which is for finding a person (APP-886). The header's count still says the shift was short.
	 */
	showsMissingRows: boolean;
};

/**
 * One job × shift-window, with its people and its holes under it.
 *
 * The header is the problem and the list under it is the fix. It states `filled/required` and nothing
 * else about the shortfall — the artifact dropped the older `−3` badge beside it deliberately, because
 * two ways of saying one thing compete for the same glance.
 *
 * The orientation note appears only when the group is open, and only when somebody in it is not
 * counted. Collapsed, a header reading `3/5` with four people under it explains nothing; that is the
 * acknowledged cost of keeping the header to one number, and the note is where it gets explained.
 *
 * A section whose job two enabled jobs share a name with cannot say which job it is, so its `+` and its missing rows'
 * Add are off, and a line under the header — shown open or folded — says why. Everything else in the group still works.
 */
export const GapsShiftGroup: FC<Props> = ({
	group,
	isOpen,
	onToggle,
	onPostShift,
	onOpenStaff,
	onAssignLocation,
	onAddToMissing,
	overtimeShiftIds,
	isDayPast,
	showsMissingRows,
}) => {
	const { t } = useTranslation();
	const { areCountsHidden } = useGapsCountsHidden();
	const { chosenDepartment } = useHospitalData();
	const { holidays } = usePageScheduleHolidays();
	const reduceMotion = useReducedMotion();
	const isTextEnlarged = useIsTextEnlarged();
	const isWriteBlocked = isSectionWriteBlocked(group);
	const blockedReasonId = useId();
	// The desktop weekly gap view's own rule and inks for this same `actual / required` — red from two
	// short, amber when over — so a window reads the same colour on both screens.
	const staffingStyle = getStaffingStatusStyle(group.actual, group.required);
	// The desktop weekly gap view's own section colours, so a job reads the same on both screens.
	const jobColor = useJobColors();
	const roleGroupColor = useRoleGroupColors();
	const tone = useMemo(
		() =>
			getSectionTone(
				{ shortRole: group.jobLabel, isJobSection: group.isJobSection, jobId: group.jobId },
				chosenDepartment.role_groups,
				jobColor,
				roleGroupColor,
			),
		[group.jobLabel, group.isJobSection, group.jobId, chosenDepartment.role_groups, jobColor, roleGroupColor],
	);

	return (
		<Box data-testid={`gaps-group-${group.key}`} sx={{ borderBottom: `1px solid ${NEUTRAL[150]}` }}>
			<Box
				data-testid={`gaps-group-header-${group.key}`}
				sx={{
					display: "flex",
					alignItems: "center",
					// The band is a translucent tint and this header is sticky — painted over an opaque base,
					// so it keeps its colour and the rows scrolling under it never show through.
					background: `linear-gradient(${tone.band}, ${tone.band}), ${SURFACE}`,
					position: "sticky",
					top: 0,
					zIndex: 2,
				}}
			>
				<ButtonBase
					data-testid={`gaps-group-toggle-${group.key}`}
					onClick={onToggle}
					aria-expanded={isOpen}
					// With no `+` after it (a past day), the counts would touch the edge: the header takes its left inset on the right too.
					sx={{
						...TOUCH_TARGET_HEIGHT_SX,
						flex: 1,
						minWidth: 0,
						display: "flex",
						alignItems: "center",
						gap: 1,
						pl: 1.5,
						pr: isDayPast ? 1.5 : 0,
						py: 1.125,
						textAlign: "left",
					}}
				>
					<ChevronRightIcon
						sx={{
							fontSize: 14,
							color: INK.MUTED,
							flex: "none",
							// A rotation is movement, so it is dropped under reduced motion; the end state still shows.
							transition: reduceMotion ? "none" : `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
							transform: isOpen ? "rotate(90deg)" : "none",
						}}
					/>
					<MetaChip
						label={group.jobLabel}
						size="small"
						customTone={{ bg: tone.badge, ink: INK.PRIMARY }}
						dataTestId={`gaps-group-job-${group.key}`}
						sx={jobBadgeSx}
					/>
					<Typography
						component="span"
						sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums" }}
					>
						{group.timeLabel}
					</Typography>
					{!areCountsHidden && (
						<Typography
							component="span"
							data-testid={`gaps-group-par-${group.key}`}
							sx={{
								flex: "none",
								fontSize: FONT_SIZE.SECONDARY,
								fontWeight: 600,
								fontVariantNumeric: "tabular-nums",
								color: staffingStyle.isFlagged ? staffingStyle.color : INK.MUTED,
							}}
						>
							{/* Filled is the figure; the requirement is its quiet denominator — until the desktop would flag it. */}
							<Box component="span" sx={{ fontWeight: 700, color: staffingStyle.isFlagged ? staffingStyle.color : INK.PRIMARY }}>
								{formatStaffCount(group.actual)}
							</Box>
							{`/${formatStaffCount(group.required)}`}
						</Typography>
					)}
					{group.openRequestCount > 0 && (
						// At Android's larger font sizes "2 posted" pushes the shift time onto two lines, so the word gives way to a
						// ringing bell — staff were alerted — which still reads "2 posted" to a screen reader.
						<MetaChip
							label={isTextEnlarged ? String(group.openRequestCount) : t("gaps.group.posted", { count: group.openRequestCount })}
							icon={isTextEnlarged ? <NotificationsActiveOutlinedIcon titleAccess={t("gaps.group.postedIcon")} /> : undefined}
							iconPlacement="end"
							tone="success"
							size="small"
							dataTestId={`gaps-group-posted-${group.key}`}
						/>
					)}
				</ButtonBase>
				{!isDayPast && (
					<GapsGroupPostButton groupKey={group.key} onClick={() => onPostShift(group)} blockedReasonId={isWriteBlocked ? blockedReasonId : undefined} />
				)}
			</Box>

			{isWriteBlocked && (
				<Box sx={{ px: 1.5, py: 0.875, bgcolor: SURFACE }}>
					<InlineHint tone="warning" dataTestId={`gaps-group-ambiguous-job-${group.key}`}>
						<Box component="span" id={blockedReasonId}>
							{t("gaps.group.ambiguousJob", { job: group.jobLabel })}
						</Box>
					</InlineHint>
				</Box>
			)}

			{isOpen && (
				<Box data-testid={`gaps-group-body-${group.key}`} sx={{ bgcolor: NEUTRAL[50] }}>
					{group.excludedFromParCount > 0 && (
						<Box
							data-testid={`gaps-group-orientation-note-${group.key}`}
							sx={{
								display: "flex",
								gap: 0.875,
								alignItems: "flex-start",
								px: 1.5,
								py: 0.875,
								bgcolor: SURFACE_TINT.LILAC_30,
								borderLeft: `${GAPS_LIST.EDGE_PX}px solid`,
								borderColor: "primary.main",
							}}
						>
							<SchoolOutlinedIcon sx={{ fontSize: 14, color: "primary.dark", flex: "none", mt: "1px" }} />
							<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY, lineHeight: 1.45 }}>
								{t("gaps.group.notCountedNote", { count: group.excludedFromParCount })}
							</Typography>
						</Box>
					)}
					{group.staff.map((entry) => (
						<GapsStaffRow
							key={entry.key}
							entry={entry}
							onOpen={onOpenStaff}
							onAssignLocation={onAssignLocation}
							isOvertime={overtimeShiftIds.has(entry.slot.nurseInShift.id)}
							accentColor={tone.badge}
							shiftHoliday={holidayForShift(entry.slot.nurseInShift, holidays)}
						/>
					))}
					{group.staff.length === 0 && (
						<Typography
							data-testid={`gaps-group-empty-${group.key}`}
							sx={{ px: 2.5, py: 3, textAlign: "center", color: INK.MUTED, fontSize: FONT_SIZE.CAPTION }}
						>
							{t("gaps.group.nobodyAssigned")}
						</Typography>
					)}
					{showsMissingRows &&
						group.missing.map((entry) => (
							<GapsMissingRow
								key={entry.key}
								entry={entry}
								jobLabel={group.jobLabel}
								onAdd={(missing) => onAddToMissing(group, missing)}
								isAddBlocked={isWriteBlocked}
							/>
						))}
				</Box>
			)}
		</Box>
	);
};
